/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { useEffect, useMemo, useState } from 'react'
import req from '@sportninja/common/api/request'
import { ROUTES } from '@sportninja/common/constants/app'
import { t } from '@sportninja/common/i18n'
import { getImageThumbnailId } from '@sportninja/common/reducers/helpers'
import colors from '@sportninja/common/constants/appColors'
import { connect } from 'react-redux'
import { useHistory } from 'react-router-dom'

import { Flex } from '../../components/Layout'
import Bio from '../../components/PageTitle/Bio'
import Icon from '../../components/Icon'
import { font } from '../../components/css'
import {
  ProfileMenu,
  ProfileName,
  ProfileTop,
  ProfileWrap,
} from '../Profile/ProfileComponents'
import dayjs from 'dayjs'
import { Alert } from 'antd'
import PlayerStatsBySeason from '../Profile/v2/PlayerStatsBySeason'

/**
 * Determines the suspension status text for a player based on their suspension data.
 * @param {Object} suspension - The suspension data object for a player.
 * @returns {string} The formatted suspension status text.
 */
const getSuspensionStatusText = (suspension) => {
  if (!suspension) {
    return null
  }
  // Check if the suspension is indefinite or has no end date
  if (suspension.enforcement?.name === 'indefinite' || !suspension.end_date) {
    return `${
      suspension?.schedule?.name_full || suspension?.schedule?.name
    } - Suspended Indefinitely`
  }
  // Format the end date for a fixed-term suspension
  return `${
    suspension?.schedule?.name_full || suspension?.schedule?.name
  } - Suspended Until ${dayjs(suspension.end_date).utc().format('MMM D, YYYY')}`
}

const Player = ({
  match: {
    params: { id },
  },
  isInFrame,
}) => {
  const history = useHistory()
  const [item, setItem] = useState({})
  const [loaded, setLoaded] = useState(false)
  const [showStats, setShowStats] = useState(true)

  const readItem = async () => {
    req(`/players/${id}/2`).then((response) => {
      setItem(response.data)
      // if (response?.data?.is_skater || response?.data?.is_goalie) {
      //   setShowStats(true)
      // }
      setLoaded(true)
    })
  }

  useEffect(() => {
    readItem()
  }, [id])

  const BASE_ROUTE = `${ROUTES.PLAYER_ROOT}/${id}`

  const pages = useMemo(
    () =>
      [
        showStats && {
          text: t('Web:statistics'),
          to: BASE_ROUTE,
          component: (
            <PlayerStatsBySeason
              playerIds={[id]}
              setShowStats={setShowStats}
              players={[item]}
              shouldQueryForBothSkaterAndGoalieStats={true}
            />
          ),
        },
      ].filter((p) => typeof p !== 'undefined' && typeof p !== 'boolean'),
    [showStats, BASE_ROUTE, id, item]
  )

  return (
    <ProfileWrap baseRoute={BASE_ROUTE} pages={pages} loaded={loaded}>
      {isInFrame && (
        <Flex
          noFlex
          css={css`
            margin: 8px 0;
          `}
        >
          <button
            type='button'
            css={css`
              padding: 8px;
              border-radius: 8px;
              border: 1px solid black;
              color: black;
              ${font.title}
              font-weight: 500;
              font-size: 14px;
            `}
            onClick={history.goBack}
          >
            <Icon
              name='caret-left'
              css={css`
                margin-right: 8px;
              `}
            />
            Back
          </button>
        </Flex>
      )}
      <ProfileTop>
        <Flex
          css={css`
            position: relative;
          `}
        >
          <img
            src={
              getImageThumbnailId(item) ||
              '/images/generic_placeholders/placeholder_player.png'
            }
            alt='Player Suspension'
            css={css`
              width: 128px;
              height: 128px;
              border-radius: 64px;
              border: 2px solid ${colors.WHITE};
            `}
            onError={(e) => {
              e.target.src =
                '/images/generic_placeholders/placeholder_player.png'
            }}
          />
          <Flex
            column
            justifyContent='center'
            css={css`
              margin-left: 16px;
            `}
          >
            <ProfileName item={item} />
            <Bio
              birthDate={item.birth_date}
              height={item.height}
              weight={item.weight}
            />

            {item.is_suspended && (
              <Alert
                showIcon
                message={getSuspensionStatusText(item?.suspensions?.[0])}
                type='error'
                css={css`
                  margin-top: 16px;
                `}
              />
            )}
          </Flex>
        </Flex>
      </ProfileTop>
      <ProfileMenu baseRoute={BASE_ROUTE} pages={pages} isInFrame={isInFrame} />
    </ProfileWrap>
  )
}

const mapStateToProps = (state) => {
  return {
    isInFrame: state.auth.inFrame,
  }
}
export default connect(mapStateToProps)(Player)
